import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';

export interface GarminStatus {
  available: boolean;
  connected: boolean;
  configured?: boolean;
  needs_reconnect?: boolean;
  rides?: number;
  ai_consent?: boolean;
  permissions?: string[];
  activity_export?: boolean;
  connected_at?: string;
  ai_consent_at?: string | null;
  last_activity_at?: string | null;
}

export const GARMIN_STATUS_KEY = ['garmin', 'status'] as const;

/**
 * Garmin Connect link for the Profile page (API ``routes/garmin.py``).
 *
 * - ``connect(aiConsent)``: records the consents and sends the browser to
 *   Garmin's sign-in; Garmin returns to the API's callback page.
 * - ``setAiConsent``: the separate, withdrawable permission for the AI
 *   coach to use Garmin rides.
 * - ``disconnect(deleteRides)``: ends the link at Garmin and here,
 *   optionally deleting the Garmin rides too.
 */
export function useGarmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addToast = useStore((s) => s.addToast);

  const status = useQuery<GarminStatus>({
    queryKey: GARMIN_STATUS_KEY,
    queryFn: () => axiosClient.get<GarminStatus>('/garmin/status').then((r) => r.data),
    staleTime: 5_000,
  });

  const connect = useMutation({
    mutationFn: (aiConsent: boolean) =>
      axiosClient
        .post<{ authorize_url: string }>('/garmin/connect', { ai_consent: aiConsent, client: 'web' })
        .then((r) => r.data),
    onSuccess: ({ authorize_url }) => {
      globalThis.location.href = authorize_url;
    },
    onError: () => addToast({ message: t('garmin.errors.connectFailed'), type: 'error' }),
  });

  const setAiConsent = useMutation({
    mutationFn: (ai: boolean) =>
      axiosClient.post<GarminStatus>('/garmin/consent', { ai }).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(GARMIN_STATUS_KEY, data);
      // Stored coach insights may have been dropped (consent withdrawn).
      queryClient.invalidateQueries({ queryKey: ['rides'] });
    },
    onError: () => addToast({ message: t('garmin.errors.consentFailed'), type: 'error' }),
  });

  const disconnect = useMutation({
    mutationFn: (deleteRides: boolean) =>
      axiosClient
        .post<{ status: string; rides_deleted: number }>('/garmin/disconnect', {
          delete_rides: deleteRides,
        })
        .then((r) => r.data),
    onSuccess: (data) => {
      addToast({
        message:
          data.rides_deleted > 0
            ? t('garmin.toasts.ridesDeleted', { count: data.rides_deleted })
            : t('garmin.toasts.disconnected'),
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: GARMIN_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: ['rides'] });
    },
    onError: () => addToast({ message: t('garmin.errors.disconnectFailed'), type: 'error' }),
  });

  return { status, connect, setAiConsent, disconnect };
}
